import { ENTITY_TYPES, MPARTICLE_EVENTS, TOGGLE_TARGETS } from 'constants/analytics';
import { MENU_OBJECT_TYPES } from 'constants/menuManagement';
import { logEvent } from 'helpers/mparticle';
import { onboardingStepFor } from 'helpers/onboardingStep';

/**
 * Centralized mParticle analytics for the Menu Editor.
 *
 * Every track* function delegates to the no-op-safe `logEvent` wrapper, so callers
 * never need to guard for the SDK being unavailable. Events must only be invoked on
 * confirmed API success (never on optimistic UI updates, undo/revert, or failures).
 */

export type AnalyticsEntityType = 'menu' | 'category' | 'item' | 'modifier_group' | 'modifier';

const ENTITY_TYPE_BY_INTERNAL: Record<string, AnalyticsEntityType> = {
  [MENU_OBJECT_TYPES.single]: 'menu',
  [MENU_OBJECT_TYPES.menus]: 'menu',
  [MENU_OBJECT_TYPES.categories]: 'category',
  [MENU_OBJECT_TYPES.items]: 'item',
  [MENU_OBJECT_TYPES.modifierGroups]: 'modifier_group',
  [MENU_OBJECT_TYPES.modifiers]: 'modifier',
};

/**
 * Maps an internal `itemType` (a `MENU_OBJECT_TYPES` value, e.g. 'cuboh_main_item') to
 * the analytics `entity_type`. Returns undefined for unknown types so the event wrapper
 * drops the attribute rather than emitting a garbage value.
 */
export function toAnalyticsEntityType(internalType?: string): AnalyticsEntityType | undefined {
  if (!internalType) return undefined;
  return ENTITY_TYPE_BY_INTERNAL[internalType];
}

/**
 * Fields compared when building `fields_changed` for an EditDrawer full-form update.
 * An explicit allowlist avoids falsely flagging derived/spread fields (parents,
 * children, imageUrl, key, uuid, ...).
 *
 * Intentionally limited to high-signal fields that compare reliably. Fields whose form
 * applies a non-empty default the raw record lacks (minimum/maximum → 0,
 * individualMinimum/Maximum → null, servingsTag → normalized) are excluded because the
 * raw-record-vs-form-default mismatch makes them over-report as "changed" on every edit.
 */
export const EDIT_DRAWER_TRACKED_FIELDS = [
  'name',
  'description',
  'price',
  'secondaryPrice',
  'enabled',
  'taxRate',
  'classifications',
] as const;

/**
 * Returns a comma-separated list of `trackedKeys` whose value differs between
 * `original` and `updated`. Uses JSON.stringify so array/object fields (e.g.
 * classifications) compare by value. Callers should pass already-normalized objects so
 * shape/default differences don't read as changes.
 */
export function diffChangedFields(
  original: object | undefined,
  updated: object | undefined,
  trackedKeys: readonly string[],
): string {
  const before = original as Record<string, unknown> | undefined;
  const after = updated as Record<string, unknown> | undefined;
  return trackedKeys
    .filter(key => JSON.stringify(before?.[key]) !== JSON.stringify(after?.[key]))
    .join(',');
}

type EntityAction = 'create' | 'update' | 'delete';

export function trackMenuEntityAction(params: {
  action: EntityAction;
  entityType?: AnalyticsEntityType;
  menuId?: string;
  entityId?: string | null;
  fieldsChanged?: string;
}): void {
  logEvent(MPARTICLE_EVENTS.entityAction, {
    action: params.action,
    entity_type: params.entityType,
    menu_id: params.menuId,
    entity_id: params.entityId ?? null,
    // Only meaningful on update; omit (undefined → dropped) on create/delete.
    fields_changed: params.action === 'update' ? params.fieldsChanged || undefined : undefined,
  });
}

export function trackMenuAvailabilityToggled(params: {
  entityType?: AnalyticsEntityType;
  availability: 'enabled' | 'disabled';
  menuId?: string;
  entityId?: string;
  /** Set when the save can fail, with `availability` the state that was asked for. */
  outcome?: 'success' | 'failed';
  /** Set by surfaces outside the menu editor so they can be told apart. See CN-51322. */
  source?: string;
}): void {
  logEvent(MPARTICLE_EVENTS.toggle, {
    entity_id: params.entityId,
    entity_type: params.entityType,
    menu_id: params.menuId,
    outcome: params.outcome,
    source: params.source,
    ...onboardingStepFor(params.source),
    status: params.availability === 'enabled' ? 'active' : 'paused',
    toggle_target: TOGGLE_TARGETS.menuAvailability,
  });
}

export type PublishOutcome = 'success' | 'partial_failure' | 'failure';

const MENU_PUBLISH_CONTENT_TYPE = 'menu_publish';

export type PublishIntegrationResult = {
  integration_name?: string;
  status_code?: number;
};

/**
 * Derives the overall publish outcome from per-integration results, then fires the event.
 * Centralizing the logic here keeps it testable in isolation.
 */
export function getPublishOutcome(responses: PublishIntegrationResult[]): PublishOutcome {
  if (!responses.length) return 'failure';
  const okCount = responses.filter(r => r.status_code === 200).length;
  if (okCount === responses.length) return 'success';
  if (okCount === 0) return 'failure';
  return 'partial_failure';
}

export function trackMenuPublished(params: {
  menuId?: string;
  responses: PublishIntegrationResult[];
  /** Set by surfaces outside the menu editor so they can be told apart. See CN-51322. */
  source?: string;
}): void {
  const { responses } = params;
  logEvent(MPARTICLE_EVENTS.contentSaved, {
    content_type: MENU_PUBLISH_CONTENT_TYPE,
    source: params.source,
    ...onboardingStepFor(params.source),
    menu_id: params.menuId,
    integration_count: responses.length,
    integrations: responses
      .map(r => r.integration_name)
      .filter(Boolean)
      .join(','),
    outcome: getPublishOutcome(responses),
  });
}

/** Coarse failure reason. Deliberately not the BE detail, which is unbounded. */
export function getPublishErrorType(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (!status) return 'request_failed';
  if (status === 400) return 'invalid_request';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status >= 500) return 'server_error';
  return 'request_failed';
}

/**
 * The publish request itself was rejected, so there are no per-integration responses to
 * derive an outcome from. Without this, publish reads as 100% successful in Amplitude.
 */
export function trackMenuPublishFailed(params: {
  menuId?: string;
  errorType: string;
  /** Set by surfaces outside the menu editor so they can be told apart. See CN-51322. */
  source?: string;
}): void {
  logEvent(MPARTICLE_EVENTS.contentSaved, {
    content_type: MENU_PUBLISH_CONTENT_TYPE,
    source: params.source,
    ...onboardingStepFor(params.source),
    menu_id: params.menuId,
    integration_count: 0,
    outcome: 'failure',
    error_type: params.errorType,
  });
}

export function trackMenuBulkPriceUpdated(params: {
  menuId?: string;
  direction: 'increase' | 'decrease';
  updateType: 'percentage' | 'fixed_cents';
  scope: 'items' | 'modifiers' | 'both';
}): void {
  logEvent(MPARTICLE_EVENTS.contentSaved, {
    content_type: 'bulk_price',
    menu_id: params.menuId,
    direction: params.direction,
    update_type: params.updateType,
    scope: params.scope,
  });
}

export function trackMenuDuplicated(params: {
  sourceMenuId?: string;
  targetMerchantCount: number;
}): void {
  logEvent(MPARTICLE_EVENTS.entityAction, {
    entity_type: ENTITY_TYPES.menu,
    action: 'duplicate',
    source_menu_id: params.sourceMenuId,
    target_merchant_count: params.targetMerchantCount,
  });
}

export function trackMenuMappingSaved(params: {
  menuId?: string;
  mappedItemCount: number;
  mappingType: 'manual' | 'auto' | 'mixed';
}): void {
  logEvent(MPARTICLE_EVENTS.contentSaved, {
    content_type: 'mapping',
    menu_id: params.menuId,
    mapped_item_count: params.mappedItemCount,
    mapping_type: params.mappingType,
  });
}

export function trackMenuPhotoEnhanceClicked(params: {
  menuId?: string;
  itemId?: string;
  companyId?: string;
}): void {
  logEvent(MPARTICLE_EVENTS.selectX, {
    name: 'menu_photo_enhance',
    source: 'menu_editor',
    menu_id: params.menuId,
    item_id: params.itemId,
    company_id: params.companyId,
  });
}

export function trackMenuPhotoSaved(params: {
  menuId?: string;
  itemId?: string;
  usedEnhancedImage: boolean;
}): void {
  logEvent(MPARTICLE_EVENTS.contentSaved, {
    content_type: 'photo',
    menu_id: params.menuId,
    item_id: params.itemId,
    used_enhanced_image: params.usedEnhancedImage,
  });
}
